/**
 * KEYNOTE cart GWP synchronizer.
 * The server-rendered keynote-gift-progress snippet determines eligibility,
 * so product tags are read by Liquid rather than guessed from /cart.js.
 */
(function () {
  if (window.KeynoteGiftsV2) return;

  const CONFIG = [
    { key: 'kiki', variantId: 67594331455762, marker: 'rm399-kiki' },
    { key: 'laundry', variantId: 67594330833170, marker: 'short-sleeve-laundry' },
  ];
  const CART_UPDATE_EVENT = 'cart:update';
  const SOURCE = 'keynote-gift-sync';
  let syncing = false;
  let debounceTimer;

  const rootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  };
  const cartUrl = (path) => rootPath() + path;

  async function requestJson(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Cart request failed');
    }
    return data;
  }

  async function getCart() {
    return requestJson(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
  }

  async function getEligibility() {
    const component = document.querySelector('cart-items-component[data-section-id]');
    const sectionId = component?.dataset.sectionId;
    if (!sectionId) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);
    const data = await requestJson(url.toString(), { headers: { Accept: 'application/json' } });
    const html = data[sectionId];
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const progress = doc.querySelector('.kgp');
    if (!progress) return { kiki: false, laundry: false };
    return {
      kiki: progress.dataset.spendUnlocked === 'true',
      laundry: progress.dataset.laundryUnlocked === 'true',
    };
  }

  async function addGifts(items) {
    if (!items.length) return;
    await requestJson(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((gift) => ({
          id: gift.variantId,
          quantity: 1,
          properties: { _keynote_gwp: gift.marker },
        })),
      }),
    });
  }

  async function changeLine(key, quantity) {
    await requestJson(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  function notifyTheme(cart) {
    document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: SOURCE,
        data: { source: SOURCE, itemCount: cart.item_count },
      },
    }));
  }

  async function sync() {
    if (syncing) return;
    syncing = true;
    try {
      const eligibility = await getEligibility();
      if (!eligibility) return;
      const cart = await getCart();
      const additions = [];
      const changes = [];

      CONFIG.forEach((gift) => {
        const wanted = eligibility[gift.key];
        const lines = cart.items.filter((item) => Number(item.variant_id) === gift.variantId);
        if (wanted && lines.length === 0) {
          additions.push(gift);
        } else if (!wanted) {
          lines.forEach((line) => changes.push({ key: line.key, quantity: 0 }));
        } else {
          lines.forEach((line, index) => {
            if (index === 0 && line.quantity > 1) changes.push({ key: line.key, quantity: 1 });
            if (index > 0) changes.push({ key: line.key, quantity: 0 });
          });
        }
      });

      let changed = false;
      if (additions.length) {
        await addGifts(additions);
        changed = true;
      }
      for (const change of changes) {
        await changeLine(change.key, change.quantity);
        changed = true;
      }
      if (changed) notifyTheme(await getCart());
    } catch (error) {
      console.error('[KeynoteGifts] Could not sync cart gifts:', error);
    } finally {
      syncing = false;
    }
  }

  function scheduleSync(event) {
    if (event?.detail?.sourceId === SOURCE || event?.detail?.data?.source === SOURCE) return;
    clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(sync, 250);
  }

  window.KeynoteGiftsV2 = { sync };
  document.addEventListener(CART_UPDATE_EVENT, scheduleSync);
  window.addEventListener('pageshow', scheduleSync);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }
})();
